/**
 * IHS-1 Overview — two team blocks side-by-side, separated by a thin
 * vertical divider. Each side shows:
 *   - Jersey/crest with rank chip overlaid bottom-right
 *   - Team name + competition subtitle below
 *   - 5-column mini-stat row (PL / V / D / L / Pts) with labels above
 *     and Pts rendered in forest green so the "current standing" reads
 *     instantly.
 *
 * Reads `data.header` for the title fall-through and resolves the
 * remaining strings via i18n keys.
 */

import type { ReactElement } from 'react';
import { useTranslation } from '../../../i18n/index.js';
import type { IhsOverviewData, IhsOverviewSide } from '../../../types/ihs.js';
import { IhsCard, dashable } from './IhsCard.js';
import { MiniStat } from '../../primitives/MiniStat.js';
import { RankChip } from '../../primitives/RankChip.js';
import { TeamCrest } from '../../primitives/TeamCrest.js';
import styles from './IhsOverview.module.css';

function SideBlock({ side }: { side: IhsOverviewSide }): ReactElement {
  const { t } = useTranslation();
  return (
    <div className={`${styles.side} stage-row`}>
      <div className={styles.crestWrap}>
        <TeamCrest
          {...(side.team.badge !== undefined ? { src: side.team.badge } : {})}
          name={side.team.name}
          size={72}
        />
        {side.rank !== null && (
          <span className={styles.rankChipPos}>
            <RankChip rank={side.rank} />
          </span>
        )}
      </div>
      <div className={styles.teamName}>{side.team.name}</div>
      <div className={styles.compLine}>{side.league.name}</div>
      <div className={styles.statRow}>
        <MiniStat
          label={t('ihs.overview.played', { defaultValue: 'PL' })}
          value={dashable(side.played)}
        />
        <MiniStat
          label={t('ihs.overview.wins', { defaultValue: 'V' })}
          value={dashable(side.wins)}
        />
        <MiniStat
          label={t('ihs.overview.draws', { defaultValue: 'D' })}
          value={dashable(side.draws)}
        />
        <MiniStat
          label={t('ihs.overview.losses', { defaultValue: 'L' })}
          value={dashable(side.losses)}
        />
        <MiniStat
          label={t('ihs.overview.points', { defaultValue: 'Pts' })}
          value={dashable(side.points)}
          valueColor="var(--accent-primary, #2A46D4)"
        />
      </div>
    </div>
  );
}

export function IhsOverview({ data }: { data: IhsOverviewData }): ReactElement {
  const { t } = useTranslation();
  const title =
    data.header?.title ??
    t('ihs.overview.title', { defaultValue: 'Overview' });
  const subtitle = data.header?.subtitle ?? data.home.league.name;
  return (
    <IhsCard title={title} subtitle={subtitle}>
      <div className={styles.duo}>
        <SideBlock side={data.home} />
        <div className={styles.vDivider} aria-hidden="true" />
        <SideBlock side={data.away} />
      </div>
    </IhsCard>
  );
}
